/**
 * Team and (optional) person pickers, shared by the decision panel and the
 * reassign card: Ivanti's teams, then the chosen team's current members.
 */

import { Field } from "@norican/react";

import { OptionSelect } from "@/features/knowledge/shared";
import { useT } from "@/i18n";

import { useAssignTeamMembers, useAssignTeams } from "./api";

export interface TeamPersonValue {
  teamId: string;
  personId: string;
}

export function TeamPersonFields({
  value,
  onChange,
  personLabel,
  personNone,
  idPrefix,
}: {
  value: TeamPersonValue;
  onChange: (value: TeamPersonValue) => void;
  personLabel: string;
  personNone: string;
  idPrefix: string;
}) {
  const { t } = useT("tickets");
  const teams = useAssignTeams();
  const members = useAssignTeamMembers(value.teamId === "" ? null : value.teamId);
  const teamOptions = (teams.data?.items ?? []).map((team) => ({
    value: team.id,
    label: team.name,
  }));
  const personOptions = [
    { value: "", label: personNone },
    ...(members.data?.items ?? []).map((m) => ({ value: m.id, label: m.name })),
  ];
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <Field name={`${idPrefix}-team`} label={t("itAdmin.assign.reassign.team")} required>
        <OptionSelect
          options={teamOptions}
          value={value.teamId}
          onValueChange={(teamId) => onChange({ teamId, personId: "" })}
          placeholder={t("itAdmin.assign.reassign.pickTeam")}
          aria-label={t("itAdmin.assign.reassign.team")}
          disabled={teams.isPending}
        />
      </Field>
      <Field name={`${idPrefix}-person`} label={personLabel}>
        <OptionSelect
          options={personOptions}
          value={value.personId}
          onValueChange={(personId) => onChange({ ...value, personId })}
          placeholder={personNone}
          aria-label={personLabel}
          disabled={value.teamId === "" || members.isPending}
        />
      </Field>
    </div>
  );
}

/** The chosen team's name and the chosen person, for a request body. */
export function useChosen(value: TeamPersonValue) {
  const teams = useAssignTeams();
  const members = useAssignTeamMembers(value.teamId === "" ? null : value.teamId);
  const team = (teams.data?.items ?? []).find((x) => x.id === value.teamId) ?? null;
  const person = (members.data?.items ?? []).find((x) => x.id === value.personId) ?? null;
  return { team, person };
}
